import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {Modal} from 'reactstrap';
import {withTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {FaIcon} from "../../FaIcon";
import {buildStackedRows} from "../columns_stacked_rows";
import {encodeColumnsToUrl} from "../../../utils/DashboardColumnsUrlParams";
import {FacetSearch, FacetFilters, TemplateSelect, SaveAsInput} from "./controls";
import {AvailableColumnsPanel, SelectedColumnsPanel} from "./panels";
import {APIClient} from "../../../utils/APIClient";
import {API_URL} from "../../../settings/local_settings";
import {AGENCY_FLEX_INDICATOR, NO_COMPARATION_PERIOD} from "../columns";
import {mergeMetricRows} from "./merge_metric_rows";
import {anchorAttr} from "../../../guides/anchors";

// Modal de Configuración de columnas. Filas estilo drawer + filtros facetados (canal/categoría) +
// búsqueda (Cmd-K) + comparativas inline + selector compacto de plantillas. Incorpora los 6
// quick-fixes pedidos por Pablo (email "UX DAshbo - Quick Fixes", 2026-06-02):
//   1) chevron a la izquierda y nombres alineados
//   2) checkbox uniforme al expandir (no pills)
//   3) sin "ver métricas avanzadas" (todo visible)
//   4) accesos directos a editores de Métricas comparativas / personalizadas
//   5) selector compacto de tableros (en vez del rail que comía pantalla)
//   6) sufijo de período en métricas de un solo período (ej. Clicks · Hoy)

const EMPTY_FILTERS = {channel: null, category: null, hasComparison: null};

function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
}

function ConfigColumnsModalComponent(props) {
    const {
        t, isOpen, onClose, activeColumns: propActiveColumns, columnsSets, activeColumnsSetId,
        handleColumnStateChange, onColumnsPositionChange,
        onSaveColumnSet, onSelectColumnSet,
        flexIndicators, ga4Indicators, ecommerceIndicators,
        paidMediaIndicators, metaIndicators, googleIndicators, tiktokIndicators,
        customMetrics, customMetricIndicators,
        canEditFlexIndicators,
    } = props;

    const [activeColumns, setActiveColumns] = useState(propActiveColumns || []);
    const [originalColumns, setOriginalColumns] = useState(propActiveColumns || []);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [showSaveAs, setShowSaveAs] = useState(false);
    const [newSetName, setNewSetName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    // Cantidad de comparativas PERSONALIZADAS del usuario (sin contar las predefinidas del
    // sistema, que están disponibles como columnas pero no cuentan contra el límite de 12).
    // El endpoint `agency/flex-indicator/` ya devuelve solo las personalizadas.
    const [customComparativeCount, setCustomComparativeCount] = useState(0);
    const searchInputRef = useRef(null);
    const wasOpenRef = useRef(false);

    // Sync incoming activeColumns when modal opens (mirrors V1 behavior)
    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            setActiveColumns([...(propActiveColumns || [])]);
            setOriginalColumns([...(propActiveColumns || [])]);
            setSearchQuery('');
            setFilters(EMPTY_FILTERS);
            setShowSaveAs(false);
            setNewSetName('');
        }
        wasOpenRef.current = isOpen;
    }, [isOpen, propActiveColumns]);

    // Al abrir, traemos cuántas comparativas personalizadas tiene el usuario para gatear el
    // límite (12) del builder inline. Solo si puede editarlas (sino el builder no se muestra).
    useEffect(() => {
        if (!isOpen || !canEditFlexIndicators) return undefined;
        let cancelled = false;
        new APIClient(`${API_URL}agency/flex-indicator/`).call('GET').then((resp) => {
            if (cancelled) return;
            const list = resp && resp.status === 'OK' && Array.isArray(resp.data) ? resp.data.filter(Boolean) : [];
            setCustomComparativeCount(list.length);
        }).catch(() => {/* el builder valida el límite igual en el submit */});
        return () => { cancelled = true; };
    }, [isOpen, canEditFlexIndicators]);

    const rows = useMemo(
        () => mergeMetricRows(buildStackedRows({t, flexIndicators, ga4Indicators, ecommerceIndicators, paidMediaIndicators, metaIndicators, googleIndicators, tiktokIndicators, customMetrics, customMetricIndicators})),
        [t, flexIndicators, ga4Indicators, ecommerceIndicators, paidMediaIndicators, metaIndicators, googleIndicators, tiktokIndicators, customMetrics, customMetricIndicators]
    );

    const hasUnsavedChanges = useMemo(
        () => activeColumnsSetId != null && !arraysEqual(activeColumns, originalColumns),
        [activeColumns, originalColumns, activeColumnsSetId]
    );

    const handleToggleColumn = useCallback((columnId, makeActive) => {
        setActiveColumns((prev) => {
            if (makeActive) {
                if (prev.includes(columnId)) return prev;
                return [...prev, columnId];
            }
            return prev.filter((id) => id !== columnId);
        });
    }, []);

    const handleRemoveColumn = useCallback((columnId) => {
        setActiveColumns((prev) => prev.filter((id) => id !== columnId));
    }, []);

    const handleReorder = useCallback((newOrder) => {
        setActiveColumns(newOrder);
    }, []);

    const handleSelectSet = useCallback((setId) => {
        const set = columnsSets && columnsSets[setId];
        const cols = set && set.columns ? set.columns : [];
        setActiveColumns([...cols]);
        setOriginalColumns([...cols]);
        if (onSelectColumnSet) onSelectColumnSet(setId);
    }, [columnsSets, onSelectColumnSet]);

    const handleApply = useCallback(() => {
        // Misma lógica que V1 handleApply: si hay set activo y sin cambios, aplicar set; sino, diff individual
        if (activeColumnsSetId != null && !hasUnsavedChanges && onSelectColumnSet) {
            onSelectColumnSet(activeColumnsSetId);
            onClose();
            return;
        }
        const propsCols = propActiveColumns || [];
        activeColumns.forEach((id) => {
            if (!propsCols.includes(id)) handleColumnStateChange(id, true);
        });
        propsCols.forEach((id) => {
            if (!activeColumns.includes(id)) handleColumnStateChange(id, false);
        });
        onColumnsPositionChange(activeColumns);
        onClose();
    }, [activeColumns, propActiveColumns, hasUnsavedChanges, activeColumnsSetId,
        handleColumnStateChange, onColumnsPositionChange, onSelectColumnSet, onClose]);

    const handleSaveAs = useCallback(() => {
        setShowSaveAs(true);
        setNewSetName('');
    }, []);

    const handleSaveNew = useCallback(() => {
        if (!newSetName.trim()) return;
        setIsSaving(true);
        const result = onSaveColumnSet(null, newSetName.trim(), activeColumns);
        const promise = result && typeof result.then === 'function' ? result : Promise.resolve(result);
        promise
            .then(() => {
                setShowSaveAs(false);
                setNewSetName('');
                setOriginalColumns([...activeColumns]);
                toast.success(t('config_columns_modal.template_saved', 'Plantilla guardada'));
            })
            .catch(() => {/* el error lo maneja Dashboard.js */})
            .finally(() => setIsSaving(false));
    }, [newSetName, activeColumns, onSaveColumnSet, t]);

    const handleUpdate = useCallback(() => {
        if (activeColumnsSetId == null) return;
        const set = columnsSets && columnsSets[activeColumnsSetId];
        if (!set) return;
        setIsSaving(true);
        const result = onSaveColumnSet(activeColumnsSetId, set.name, activeColumns);
        const promise = result && typeof result.then === 'function' ? result : Promise.resolve(result);
        promise
            .then(() => {
                setOriginalColumns([...activeColumns]);
                toast.success(t('config_columns_modal.template_updated', 'Plantilla actualizada'));
            })
            .catch(() => {})
            .finally(() => setIsSaving(false));
    }, [activeColumnsSetId, columnsSets, activeColumns, onSaveColumnSet, t]);

    const handleCopyLink = useCallback(() => {
        const url = encodeColumnsToUrl(activeColumns);
        navigator.clipboard.writeText(url).then(() => {
            toast.success(t('config_columns_modal.columns_link_copied', 'Link copiado'));
        });
    }, [activeColumns, t]);

    // Crea una comparativa (la misma métrica en otro período, con/sin comparación) a nivel agencia.
    // Las comparativas se PERSISTEN vía `agency/flex-indicator/update-all` (reemplaza todo el set,
    // máx 12): leemos el set actual, le sumamos la nueva sin pisar las existentes, y la agregamos
    // como columna seleccionada. Devuelve una promesa (el builder cierra al resolver).
    const handleCreateComparative = useCallback(({metricName, mainPeriod, vsPeriod}) => {
        const vs = vsPeriod || null;
        const columnId = `${AGENCY_FLEX_INDICATOR}||${metricName}||${mainPeriod}||${vs || NO_COMPARATION_PERIOD}`;
        const addColumn = () => setActiveColumns((prev) => (prev.includes(columnId) ? prev : [...prev, columnId]));
        const sameEntry = (e) => e && e.metric_name === metricName && e.main_period === mainPeriod && (e.vs_period || null) === vs;
        const errMsg = t('config_columns_modal.comparative_error', 'No se pudo crear la comparativa.');

        return new APIClient(`${API_URL}agency/flex-indicator/`).call('GET').then((resp) => {
            if (!resp || resp.status !== 'OK') {
                throw new Error((resp && resp.error_message) || errMsg);
            }
            const current = (Array.isArray(resp.data) ? resp.data : []).filter(Boolean);
            if (current.some(sameEntry)) {
                addColumn();
                toast.success(t('config_columns_modal.comparative_exists', 'Esa comparativa ya existía; la agregué a tus columnas.'));
                return;
            }
            if (current.length >= 12) {
                throw new Error(t('config_columns_modal.comparative_max', 'Llegaste al máximo de 12 métricas comparativas. Borrá alguna en el editor para crear otra.'));
            }
            const next = current.map((e) => ({metric_name: e.metric_name, main_period: e.main_period, vs_period: e.vs_period || null}));
            next.push({metric_name: metricName, main_period: mainPeriod, vs_period: vs});
            while (next.length < 12) next.push(null);
            return new APIClient(`${API_URL}agency/flex-indicator/update-all`).call('PUT', next).then((res) => {
                if (res && res.status === 'OK') {
                    addColumn();
                    setCustomComparativeCount(current.length + 1);
                    toast.success(t('config_columns_modal.comparative_created', 'Comparativa creada y agregada a tus columnas.'));
                } else {
                    throw new Error((res && res.error_message) || errMsg);
                }
            });
        }).catch((err) => {
            toast.error((err && err.message) || errMsg);
            throw err;
        });
    }, [t]);

    const activeSetName = useMemo(() => {
        if (activeColumnsSetId == null) return t('config_columns_modal.custom_view', 'Vista personalizada');
        const set = columnsSets && columnsSets[activeColumnsSetId];
        return set ? set.name : t('config_columns_modal.custom_view', 'Vista personalizada');
    }, [activeColumnsSetId, columnsSets, t]);

    return (
        <Modal isOpen={isOpen} toggle={onClose} className="ccm-modal" size="xl">
            <div className="ccm">
                <header className="ccm-header">
                    <FaIcon iconName="columns" className="ccm-header__icon"/>
                    <h2 className="ccm-header__title">
                        {t('config_columns_modal.title', 'Configuración de columnas')}
                    </h2>
                    <span className="ccm-header__subtitle">{activeSetName}</span>
                    {hasUnsavedChanges && (
                        <span className="ccm-header__dot" title={t('config_columns_modal.unsaved_changes', 'Cambios sin guardar')}/>
                    )}
                    <button type="button" className="ccm-header__close" onClick={onClose} aria-label="Cerrar">
                        <FaIcon iconName="times"/>
                    </button>
                </header>

                <div className="ccm-toolbar">
                    <FacetSearch
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        searchInputRef={searchInputRef}
                        t={t}
                    />
                    <FacetFilters filters={filters} onChange={setFilters} t={t}/>
                    {/* Cambio 5: selector compacto de tableros guardados (reemplaza el rail).
                        El alta de plantilla queda sólo en el footer ("Guardar como plantilla"). */}
                    <TemplateSelect
                        columnsSets={columnsSets}
                        activeColumnsSetId={activeColumnsSetId}
                        onSelectSet={handleSelectSet}
                        t={t}
                    />
                </div>

                {/* Cambio 5: body de 2 columnas (sin TemplatesRail) */}
                <div className="ccm-body">
                    <AvailableColumnsPanel
                        rows={rows}
                        activeColumns={activeColumns}
                        searchQuery={searchQuery}
                        filters={filters}
                        onToggleColumn={handleToggleColumn}
                        onClose={onClose}
                        canCreateComparative={!!canEditFlexIndicators}
                        onCreateComparative={handleCreateComparative}
                        comparativeCount={customComparativeCount}
                        t={t}
                    />
                    <SelectedColumnsPanel
                        activeColumns={activeColumns}
                        onColumnsPositionChange={handleReorder}
                        onColumnRemove={handleRemoveColumn}
                        onCopyLink={handleCopyLink}
                        t={t}
                    />
                </div>

                <footer className="ccm-footer">
                    {showSaveAs ? (
                        <SaveAsInput
                            newSetName={newSetName}
                            onChange={setNewSetName}
                            onSave={handleSaveNew}
                            onCancel={() => setShowSaveAs(false)}
                            isSaving={isSaving}
                            t={t}
                        />
                    ) : (
                        <div className="ccm-footer__actions">
                            {activeColumnsSetId != null && hasUnsavedChanges && (
                                <button type="button" className="btn -alt -negative" onClick={handleUpdate} disabled={isSaving}>
                                    {isSaving ? t('common.saving', 'Guardando…') : t('config_columns_modal.update_template', 'Actualizar plantilla')}
                                </button>
                            )}
                            <button type="button" className="btn -negative" onClick={handleSaveAs}
                                    {...anchorAttr('columns_save_as')}>
                                {t('config_columns_modal.save_as_new', 'Guardar como plantilla')}
                            </button>
                            <button type="button" className="btn -alt" onClick={onClose}>
                                {t('common.cancel', 'Cancelar')}
                            </button>
                            <button type="button" className="btn" onClick={handleApply}>
                                {t('common.apply', 'Aplicar')}
                            </button>
                        </div>
                    )}
                </footer>
            </div>
        </Modal>
    );
}

export const ConfigColumnsModal = withTranslation()(ConfigColumnsModalComponent);
export default ConfigColumnsModal;
